/**
 * Reading members out of a mounted DAT2 archive, in the browser.
 *
 * `src/import/dat.ts` only *sniffs* an archive: the import path hands whole
 * .dat files to the engine and lets `xfile.cc` do the reading, so nothing on
 * the shell side ever needed a member out of one. The save editor does — the
 * GVAR name table, the item protos, the item names and the inventory art all
 * live inside master.dat on a normal install, and a DAT-only install is the
 * common case, not the exotic one.
 *
 * The format is the same tail-anchored tree `dat.ts` documents and
 * `scripts/dat2.mjs` walks on the Node side:
 *
 *     [ file data ... ][ tree ][ treeSize:u32le ][ totalSize:u32le ]
 *     tree := count:u32le, then per entry:
 *       nameLen:u32le, name (backslash-separated), packed:u8,
 *       realSize:u32le, packedSize:u32le, offset:u32le
 *
 * Two things make this different from the Node version and both are forced:
 *
 *   - **Ranged reads, never `readFile`.** master.dat is 333MB and lives in
 *     MEMFS. `FS.readFile` copies the whole thing into the JS heap to hand back
 *     four hundred bytes of proto. `FS.open`/`FS.read` reads the slice, which
 *     is a memcpy. An engine whose FS lacks `read` gets no archives rather than
 *     an out-of-memory tab.
 *   - **Inflate is async.** DAT2 payloads are zlib streams and the browser's
 *     only decompressor is `DecompressionStream`, which is a stream. Every
 *     member read is therefore a promise, and that asynchrony propagates all
 *     the way up to the editor's panel — which is why the editor renders first
 *     and names its globals a moment later.
 *
 * Nothing here throws for a missing member or a malformed archive: an install
 * with a damaged patch000.dat should degrade to numeric GVARs and an empty
 * catalog, not to a panel that will not open.
 */

import type { EmFS } from '../engine';
import { openDat1Archive, openDat1ArchiveBlob } from './dat1';

export interface DatEntry {
  packed: boolean;
  realSize: number;
  packedSize: number;
  offset: number;
  /**
   * DAT1 only: the raw `dir_entry.flags`, whose `& 0xF0` picks between the three
   * storage modes (`db_read_to_buf`, fallout1-ce db.cc:445). DAT2 has no such
   * field — its `packed` byte is the whole story — so this is undefined there.
   */
  dat1Flags?: number;
}

export interface DatArchive {
  /** Path of the .dat on the mounted filesystem. For diagnostics. */
  readonly path: string;
  /** Lowercased forward-slash member path -> entry. */
  readonly entries: ReadonlyMap<string, DatEntry>;
  has(member: string): boolean;
  /** Member paths starting with `prefix` (lowercase, forward slashes), sorted. */
  list(prefix: string): string[];
  /** The member's bytes, or null when it is not in this archive. */
  read(member: string): Promise<Uint8Array | null>;
}

/** Members bigger than this are not something the editor asks for. */
const MAX_MEMBER_BYTES = 32 * 1024 * 1024;
/** A tree larger than this is a corrupt footer, not a big archive. */
const MAX_TREE_BYTES = 64 * 1024 * 1024;
const MAX_FILE_COUNT = 500_000;

const normalize = (member: string): string => member.replace(/\\/g, '/').toLowerCase();

/**
 * A ranged reader over one file on the mounted filesystem.
 *
 * Returns null when the FS cannot do ranged reads at all, which is the one case
 * where falling back to `readFile` would be actively harmful.
 */
function rangedReader(FS: EmFS, path: string): ((start: number, length: number) => Uint8Array) | null {
  if (typeof FS.open !== 'function' || typeof FS.read !== 'function' || typeof FS.close !== 'function') {
    return null;
  }
  return (start, length) => {
    const out = new Uint8Array(length);
    if (length === 0) return out;
    const stream = FS.open!(path, 'r');
    try {
      let got = 0;
      while (got < length) {
        const n = FS.read!(stream, out, got, length - got, start + got);
        if (n <= 0) break;
        got += n;
      }
      return got === length ? out : out.subarray(0, got);
    } finally {
      FS.close!(stream);
    }
  };
}

async function inflate(bytes: Uint8Array): Promise<Uint8Array | null> {
  const DS = (globalThis as { DecompressionStream?: typeof DecompressionStream }).DecompressionStream;
  if (!DS) return null;
  try {
    // A fresh Uint8Array, because the stream keeps a reference and `bytes` is a
    // view into a buffer this module reuses.
    const stream = new Blob([bytes.slice()]).stream().pipeThrough(new DS('deflate'));
    const chunks: Uint8Array[] = [];
    let total = 0;
    const reader = (stream as ReadableStream<Uint8Array>).getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value?.length) continue;
      chunks.push(value);
      total += value.length;
      if (total > MAX_MEMBER_BYTES) return null;
    }
    const out = new Uint8Array(total);
    let at = 0;
    for (const c of chunks) {
      out.set(c, at);
      at += c.length;
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * Opens a mounted .dat and reads its directory tree. Returns null for anything
 * that is not a readable DAT2 — a missing file, a truncated one, an FS that
 * cannot seek.
 */
export async function openDatArchive(FS: EmFS, path: string): Promise<DatArchive | null> {
  let size: number;
  try {
    size = FS.stat(path).size;
  } catch {
    return null;
  }

  const readRange = rangedReader(FS, path);
  if (!readRange) return null;

  return openDatArchiveCore(path, size, readRange);
}

/**
 * The same archive over a Blob — the vault's own storage form. This is what
 * lets the Mod Maker read protos out of master.dat before any engine has
 * booted: `Blob.slice` is the ranged read, no filesystem involved.
 */
export function openDatArchiveBlob(path: string, blob: Blob): Promise<DatArchive | null> {
  return openDatArchiveCore(path, blob.size, async (start, length) => {
    return new Uint8Array(await blob.slice(start, start + length).arrayBuffer());
  });
}

async function openDatArchiveCore(
  path: string,
  size: number,
  readRange: (start: number, length: number) => Uint8Array | Promise<Uint8Array>
): Promise<DatArchive | null> {
  if (size < 1024) return null;

  const footer = await readRange(size - 8, 8);
  if (footer.length !== 8) return null;
  const fv = new DataView(footer.buffer, footer.byteOffset, footer.byteLength);
  const treeSize = fv.getUint32(0, true);
  const totalSize = fv.getUint32(4, true);
  // Same footer check `readDat2Info` makes: `totalSize` doubles as a checksum,
  // so a truncated or half-written archive fails here rather than producing a
  // tree of nonsense.
  if (totalSize !== size) return null;
  if (treeSize < 4 || treeSize > size - 8 || treeSize > MAX_TREE_BYTES) return null;

  const tree = await readRange(size - 8 - treeSize, treeSize);
  if (tree.length !== treeSize) return null;
  const tv = new DataView(tree.buffer, tree.byteOffset, tree.byteLength);

  const count = tv.getUint32(0, true);
  if (count === 0 || count > MAX_FILE_COUNT) return null;

  const entries = new Map<string, DatEntry>();
  let p = 4;
  for (let i = 0; i < count; i++) {
    if (p + 4 > treeSize) return null;
    const nameLen = tv.getUint32(p, true);
    p += 4;
    if (nameLen < 1 || nameLen > 255 || p + nameLen + 13 > treeSize) return null;
    let name = '';
    for (let k = 0; k < nameLen; k++) name += String.fromCharCode(tree[p + k]!);
    p += nameLen;
    const packed = tree[p]! !== 0;
    p += 1;
    const realSize = tv.getUint32(p, true);
    const packedSize = tv.getUint32(p + 4, true);
    const offset = tv.getUint32(p + 8, true);
    p += 12;
    entries.set(normalize(name), { packed, realSize, packedSize, offset });
  }

  const sorted = [...entries.keys()].sort();

  return {
    path,
    entries,
    has: (member) => entries.has(normalize(member)),
    list: (prefix) => {
      const want = normalize(prefix);
      return sorted.filter((k) => k.startsWith(want));
    },
    async read(member) {
      const e = entries.get(normalize(member));
      if (!e) return null;
      const length = e.packed ? e.packedSize : e.realSize;
      if (length > MAX_MEMBER_BYTES || e.realSize > MAX_MEMBER_BYTES) return null;
      if (e.offset + length > size) return null;
      const raw = await readRange(e.offset, length);
      if (raw.length !== length) return null;
      if (!e.packed) return raw;
      const out = await inflate(raw);
      // A member whose inflated length disagrees with the tree is a member we
      // read at the wrong offset. Refusing beats handing back half a proto.
      return out && out.length === e.realSize ? out : null;
    },
  };
}

/**
 * The archive at `path`, whichever container it is.
 *
 * A DAT2's footer check (`totalSize` must equal the file size) is both cheap and
 * strict enough to be a sniff on its own, so it is tried first and a DAT1 only
 * ever reaches the DAT1 reader after DAT2 has declined. That ordering is what
 * keeps the Fallout 2 path byte-identical: for a real DAT2 this function is
 * `openDatArchive` with one extra comparison that always succeeds.
 */
export async function openAnyArchive(FS: EmFS, path: string): Promise<DatArchive | null> {
  const dat2 = await openDatArchive(FS, path);
  if (dat2) return dat2;
  return openDat1Archive(FS, path);
}

/** The same over a Blob, for the vault's pre-boot reads. */
export async function openAnyArchiveBlob(path: string, blob: Blob): Promise<DatArchive | null> {
  const dat2 = await openDatArchiveBlob(path, blob);
  if (dat2) return dat2;
  return openDat1ArchiveBlob(path, blob);
}
